import throttle from "lodash.throttle"
import { formatNumbers } from "../helpers/_formatters"
import objectFromEntries from "../helpers/_objectFromEntries"
import { CLASSES } from "../_consts"

/* eslint-disable no-underscore-dangle */

const updateActions = {
  addProduct: 'addProduct',
  removeProduct: 'removeProduct',
  changeProductCount: 'changeProductCount',
  changePromocode: 'changePromocode',
  changeCertificate: 'changeCertificate',
  changeBonus: 'changeBonus',
  clearAll: 'clearAll',
}

const discountOptions = {
  promocode: 'promocode',
  certificate: 'certificate',
  bonus: 'bonus',
}

const {lang} = document.documentElement

const defineEndings = (num) => {
  const lastNum = +num.toString().slice(-1)

  const wordEndings = {
    'ru': ['а', 'ов'],
    'uk-UA': ['и', 'ів'],
  }

  if (num > 4 && num < 21) {
    return wordEndings[lang][1]
  } if (lastNum === 1) {
    return ''
  } if (lastNum > 1 && lastNum < 5) {
    return wordEndings[lang][0]
  }

  return wordEndings[lang][1]
}

const basePath = '/api/cart'
// const basePath = 'http://test.loc/api/cart' // for local test

const initialState = {
  info: {},
  products: [],
  promocode: {
    discount: null,
    value: '',
  },
  certificate: {
    discount: null,
    value: {
      id: '',
      code: '',
    },
  },
  bonus: {
    discount: null,
    value: '',
  },
}

const cartStore = {
  _state: initialState,
  _subscribers: [],

  getState() {
    return this._state
  },
  setState(newState) {
    this._state = newState
  },

  _getData() {
    function getCart(callback) {
      $.ajax({
        type: 'GET',
        dataType: 'json',
        url: `${basePath}/getAll.php`,
        headers: {
          'Content-Type': 'application/json'
        },
        success(cartData) {
          if (cartData) {
            callback(cartData)
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    getCart(newState => {
      this._state = newState
      this._updateView()
    })
  },
  _setData(cb) {
    function clearCart(callback) {
      $.ajax({
        type: 'GET',
        dataType: 'json',
        url: `${basePath}/clearAll.php`,
        headers: {
          'Content-Type': 'application/json'
        },
        success(cartData) {
          if (cartData) {
            callback(cartData)
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    clearCart(cb)
  },
  clearStorage() {
    this._setData(data => {
      this._state = data
      this._updateView(updateActions.clearAll)
    })
  },

  subscribe(callback) {
    this._subscribers = [...this._subscribers, callback]

    // immediately call the callback, since you need to immediately update the view
    // no need to update the whole view so that it doesn't update multiple times
    const data = this._getCalculatedData()
    callback(data)
  },

  _getPricePartWithPromo() {
    /* eslint-disable no-return-assign, no-param-reassign */
    return this._state.products.reduce((sum, item) =>
      item.prices.old == null ? sum += item.prices.current * item.count : sum += 0
    , 0)
    /* eslint-enable no-return-assign, no-param-reassign */
  },
  _getPricePartWithoutPromo() {
    /* eslint-disable no-return-assign, no-param-reassign */
    return this._state.products.reduce((sum, item) =>
      item.prices.old != null ? sum += item.prices.current * item.count : sum += 0
    , 0)
    /* eslint-enable no-return-assign, no-param-reassign */
  },
  _getCalculatedData() {
    /* eslint-disable no-return-assign, no-param-reassign */
    const fullPrice = this._state.products.reduce((sum, item) =>
      sum += item.prices.current * item.count
    , 0)

    let discountPercent = 0

    const calcDiscount = (price, {sum, min, max}) => {
      min = min || -Infinity
      max = max || Infinity

      if (price > min && price < max) {
        discountPercent = +sum / (fullPrice / 100)
        return price - +sum
      }
      return price
    }

    const pricePartWithPromo = this._getPricePartWithPromo()

    const pricePartWithoutPromo = this._getPricePartWithoutPromo()

    let finalPrice = 0
    if (this.isPromocode()) {
      finalPrice = calcDiscount(pricePartWithPromo, this._state.promocode.discount) + pricePartWithoutPromo
    } else {
      finalPrice = pricePartWithPromo + pricePartWithoutPromo
    }

    if (this.isCertificate()) {
      finalPrice = Math.max(finalPrice - this._state.certificate.discount, 0)
    }

    if (this.isBonus()) {
      const bonus = this._state.bonus.discount.current
      const halfFinalPrice = Math.floor(finalPrice / 2)

      if (bonus > halfFinalPrice) {
        this.changeDiscount(null, discountOptions.bonus, () => {
          const btn = document.querySelector(`button[data-target="[data-cart-option='${discountOptions.bonus}']"]`)
          window.cartModalView.openDiscountOptionsTab(btn)
          window.cartModalView.showBonusAlert()
        })
      }

      finalPrice = Math.max(finalPrice - bonus, 0)
    }

    /* eslint-enable no-return-assign, no-param-reassign */

    return {
      products: [...this._state.products],
      fullPrice,
      promocode: {
        discount: Object.assign({}, this._state.promocode.discount, {discountPercent}),
        value: this._state.promocode.value,
      },
      certificate: {
        discount: this._state.certificate.discount,
        value: this._state.certificate.value,
      },
      bonus: {
        discount: this._state.bonus.discount,
        value: this._state.bonus.value,
      },
      finalPrice,
      isEmpty: this.isEmpty(),
      isPromocode: this.isPromocode(),
      isCertificate: this.isCertificate(),
      isBonus: this.isBonus(),
    }
  },
  _updateView(action, payload = null) {
    const data = this._getCalculatedData()

    this._subscribers.forEach(subscriber => {
      subscriber(data, {type: action, payload})
    })
  },

  addProduct(productId, inputsValues, cb) {
    const getProductById = (id, extraData, callback) => {
      $.ajax({
        type: 'POST',
        dataType: 'json',
        url: `${basePath}/add.php`,
        data: JSON.stringify({id, extraData}),
        headers: {
          'Content-Type': 'application/json'
        },
        success: (product) => {
          if (product) {
            const sameProduct = this._state.products.find(item =>
              item.id === product.id
            )

            if (sameProduct) {
              this._state.products = this._state.products.map(item => {
                return item.id === product.id ? product : item
              })

              this._updateView(updateActions.changeProductCount, {
                id: product.id,
                count: product.count,
              })
            } else {
              this._state.products.push(product)

              this._updateView(updateActions.addProduct, product)
            }

            callback()
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    getProductById(productId, inputsValues, cb)
  },
  removeProduct(productId) {
    function removeItem(id, callback) {
      $.ajax({
        type: 'POST',
        dataType: 'json',
        data: JSON.stringify({id}),
        url: `${basePath}/remove.php`,
        headers: {
          'Content-Type': 'application/json'
        },
        success(data) {
          if (data) {
            callback(data)
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    removeItem(productId, data => {
      if (data.status === 'success') {
        this._state.products = this._state.products.filter(item =>
          item.id !== productId
        )
        this._updateView(updateActions.removeProduct, productId)
      }
    })
  },
  changeProductCount(productId, productCount) {
    function changeCount(id, count, callback) {
      $.ajax({
        type: 'POST',
        dataType: 'json',
        data: JSON.stringify({id, count}),
        url: `${basePath}/changeCount.php`,
        headers: {
          'Content-Type': 'application/json'
        },
        success(data) {
          if (data) {
            callback(data)
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    changeCount(productId, productCount, data => {
      if (data.status === 'success') {
        this._state.products = this._state.products.map(item => {
          if (item.id === productId) {
            return Object.assign({}, item, {count: productCount})
          }

          return item
        })

        this._updateView(updateActions.changeProductCount, {
          id: productId,
          count: productCount,
        })
      }
    })
  },
  changeDiscount(discountValue, optionName, cb) {
    if (!optionName) return

    let updateView
    let postUrl
    let updateState

    if (optionName === discountOptions.promocode) {
      updateView = this._updateView.bind(this, updateActions.changePromocode)

      postUrl = `${basePath}/set${optionName.charAt(0).toUpperCase() + optionName.slice(1)}.php`

      updateState = (discount, value) => {
        this._state.promocode.discount = discount
        this._state.promocode.value = value
      }
    } else if (optionName === discountOptions.certificate) {
      updateView = this._updateView.bind(this, updateActions.changeCertificate)

      postUrl = `${basePath}/set${optionName.charAt(0).toUpperCase() + optionName.slice(1)}.php`

      updateState = (discount, value) => {
        this._state.certificate.discount = discount
        this._state.certificate.value = value
      }
    } else if (optionName === discountOptions.bonus) {
      updateView = this._updateView.bind(this, updateActions.changeBonus)

      postUrl = `${basePath}/set${optionName.charAt(0).toUpperCase() + optionName.slice(1)}.php`

      updateState = (discount, value) => {
        this._state.bonus.discount = discount
        this._state.bonus.value = value
      }
    } else {
      return
    }

    const areAllProductsDiscounted = this._areAllProductsDiscounted.bind(this)
    const getPricePartWithPromo = this._getPricePartWithPromo.bind(this)

    function removeDiscount(callback, update = false) {
      $.ajax({
        type: 'POST',
        dataType: 'json',
        data: JSON.stringify({value: null}),
        url: postUrl,
        headers: {
          'Content-Type': 'application/json'
        },
        success(data) {
          if (data) {
            if (callback) callback(data)

            if (update && data.status === 'success') {
              updateState(data.discount, null)
              updateView()
            }
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    function getDiscount(value, callback) {
      $.ajax({
        type: 'POST',
        dataType: 'json',
        data: JSON.stringify({value}),
        url: postUrl,
        headers: {
          'Content-Type': 'application/json'
        },
        success(data) {
          if (data) {
            let processedData = Object.assign({}, data)

            if (optionName === discountOptions.promocode) {
              const priceWithPromo = getPricePartWithPromo()

              // reset if the total amount doesn't fit the limits of the promocode
              if (areAllProductsDiscounted() || processedData.discount && (priceWithPromo < processedData.discount.min || priceWithPromo > processedData.discount.max)) {
                processedData = Object.assign({}, data, {discount: null, inapplicable: true})
                removeDiscount()
              }
            }

            callback(processedData)

            if (processedData.status === 'success') {
              updateState(processedData.discount, value)
              updateView()
            }
          } else {
            console.error('Something is wrong on the server')
          }
        },
        error(jqxhr, text, exeption) {
          const error = {jqxhr, text, exeption}
          throw(error)
        },
      })
    }

    if (discountValue !== undefined || discountValue !== null) {
      getDiscount(discountValue, cb)
    } else {
      removeDiscount(cb, true)
    }
  },

  isEmpty() {
    return !this._state.products.length
  },
  isPromocode() {
    return !!this._state.promocode.discount
  },
  isCertificate() {
    return !!this._state.certificate.discount
  },
  isBonus() {
    return !!this._state.bonus.discount
  },
  _areAllProductsDiscounted() {
    return this._state.products.every((product) => {
      return product.prices.old
    })
  },

  init() {
    this._getData()

    this.changeProductCount = throttle(this.changeProductCount, 500)
  },
}

const cartModalView = {
  _selectors: {
    modal: '.js-cart-modal',
    productAddForm: '.js-cart-modal-product-add-form',
    productAddFormTooltip: '.js-cart-modal-product-add-form-tooltip',
    productAddFormChangeable1: '.js-cart-modal-product-add-form-changeable-1',
    productAddFormChangeable2: '.js-cart-modal-product-add-form-changeable-2',
    discountOptions: '.js-cart-modal-options',
    discountOptionsBtn: '.js-cart-modal-options-btn',
    discountOption: '.js-cart-modal-option',
    promocodeWarning: '.js-cart-modal-promo-warning',
    preResultsSection: '.js-cart-modal-pre-results',
    preResultsPrice: '.js-cart-modal-pre-results-price',
    preResultsPricePromocode: '.js-cart-modal-pre-results-promocode',
    preResultsPriceCertificate: '.js-cart-modal-pre-results-certificate',
    preResultsPriceBonus: '.js-cart-modal-pre-results-bonus',
    fullPrice: '.js-cart-modal-results-fullprice',
    cartItems: '.js-cart-modal-items',
    cartItem: '.js-cart-modal-item',
    cartItemCount: '.js-cart-item-count',
    cartItemPrice: '.js-cart-modal-item-final-price',
    btnsTooltipOpener: '.js-cart-modal-btn-tooltip-opener',
    cartItemRemoveBtn: '.js-cart-modal-remove-btn',
    cartItemFavoriteBtn: '.js-cart-modal-favorite-btn',
  },

  _changeWhetherEmpty() {
    const emptyBlock = document.querySelector(`${this._selectors.modal}-empty`)
    const notEmptyBlock = document.querySelector(`${this._selectors.modal}-not-empty`)

    if (this._store.isEmpty()) {
      emptyBlock.classList.add(CLASSES.isActive)
      notEmptyBlock.classList.remove(CLASSES.isActive)
    } else {
      emptyBlock.classList.remove(CLASSES.isActive)
      notEmptyBlock.classList.add(CLASSES.isActive)
    }
  },
  _openModal() {
    const modal = document.querySelector(this._selectors.modal)
    window.modal.open(modal, false)
  },

  _insertNewProductsItemHTML(product) {
    const container = document.querySelector(this._selectors.cartItems)
    if (!container) return

    const cartState = this._store.getState()

    const priceMarkup = product.prices.old ? `
      <div class="cart-modal__item-price">
        <span class="cart-modal__item-price-discount">
          ${ formatNumbers(product.prices.current) } ${ cartState.info.currency }
          ${
            product.prices.current !== product.prices.old
              ? `<span>${ formatNumbers(product.prices.old) } ${ cartState.info.currency }</span>`
              : ``
          }
          <div class="cart-modal__item-promo-warning js-cart-modal-promo-warning">
            <div class="main-tooltip main-tooltip--cart-promo-warning">
              <div class="main-tooltip__arrow"></div>
              <div class="main-tooltip__content">
                <p>${ cartState.info.popups.promo[lang].text }</p>
              </div>
            </div>
          </div>
        </span>
      </div>` : `
      <div class="cart-modal__item-price">
        ${ formatNumbers(product.prices.current) } ${ cartState.info.currency }
      </div>`

    const itemLabel = product.label ?
      `<div class="cart-modal__item-label">${ product.label.text }</div>` : ``

    const itemMarkup = `
      <div class="cart-modal__item js-cart-modal-item" data-product-id="${ product.id }">
        <div class="cart-modal__item-info">
          <div class="cart-modal__item-img">
            <img class="img-responsive" src="${ product.images.big.src }" alt="${ product.images.big.alt }">
          </div>
          <div class="cart-modal__item-descr">
            ${ itemLabel }
            <div class="cart-modal__item-title">
              <a href="${ product.titleLink }">${ product.title }</a>
            </div>
            <div class="cart-modal__item-article">${ cartState.info.article.text[lang].text }: ${ product.article }</div>
            ${ priceMarkup }
          </div>
        </div>
        <div class="cart-modal__item-params">
          <div class="cart-modal__item-remove js-cart-modal-btn-tooltip-opener">
            <button class="remove-btn">
              <svg class="icon icon-trash"><use xlink:href="/img/sprite.svg#icon-trash"></use></svg>
            </button>
            <div class="cart-modal__item-remove-tooltip">
              <div class="main-tooltip main-tooltip--cart">
                <div class="main-tooltip__arrow"></div>
                <div class="main-tooltip__content content content--description">
                  <button class="main-tooltip__icon-btn main-tooltip__icon-btn--close js-cart-modal-remove-btn">
                    <svg class="icon icon-cross"><use xlink:href="/img/sprite.svg#icon-cross"></use></svg>${ cartState.info.popups.remove[lang].removeText }
                  </button>
                  <button class="main-tooltip__icon-btn main-tooltip__icon-btn--favorite ${ product.inFavorites ? 'is-active ' : '' }js-cart-modal-favorite-btn">
                    <svg class="icon icon-heart"><use xlink:href="/img/sprite.svg#icon-heart"></use></svg>${ cartState.info.popups.remove[lang].favoriteText }
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="num-input js-num-input cart-modal__item-count js-cart-item-count">
            <button>-</button>
            <input type="number" value="${ product.count }" min="1" max="1000">
            <button>+</button>
          </div>
          <div class="cart-modal__item-full-price"><b class="js-cart-modal-item-final-price">${ formatNumbers(product.prices.current * product.count) }</b> грн</div>
        </div>
      </div>`

    container.insertAdjacentHTML('beforeend', itemMarkup)
  },
  _removeProductFromHTML(id) {
    const cartItems =
      [].slice.call(document.querySelectorAll(this._selectors.cartItem))

    cartItems.forEach(item => {
      if (item.dataset.productId === id) {
        item.remove()
      }
    })
  },
  _changeProductCountInHTML(id, count) {
    const cartItem =
      document.querySelector(`${this._selectors.cartItem}[data-product-id="${id}"`)

    if (cartItem) {
      const cartItemCount = cartItem.querySelector(this._selectors.cartItemCount)
      const input = cartItemCount.querySelector('input')

      if (+input.value !== count) {
        input.value = count
      }
    }
  },
  _changeProductPriceInHTML(id, count, price) {
    const cartItem =
      document.querySelector(`${this._selectors.cartItem}[data-product-id="${id}"`)

    if (cartItem) {
      const cartItemPrice = cartItem.querySelector(`${this._selectors.cartItemPrice}`)

      cartItemPrice.textContent = formatNumbers(price * count)
    }
  },

  _changePromoTextInHTML(value) {
    const option = document.querySelector(`${this._selectors.discountOption}[data-cart-option=${discountOptions.promocode}]`)
    const output = option.querySelector(`${this._selectors.discountOption}-results-text`)

    output.textContent = value
  },
  _changeCertificateTextInHTML(value) {
    const option = document.querySelector(`${this._selectors.discountOption}[data-cart-option=${discountOptions.certificate}]`)
    const output = option.querySelector(`${this._selectors.discountOption}-results-text`)

    output.textContent = value
  },
  _changeBonusTextInHTML(value) {
    const option = document.querySelector(`${this._selectors.discountOption}[data-cart-option=${discountOptions.bonus}]`)
    const output = option.querySelector(`${this._selectors.discountOption}-results-text`)

    output.textContent = value
  },
  _showDiscountOptionSuccessfullState(optionName = null) {
    const sections = [].slice.call(document.querySelectorAll(this._selectors.discountOption))

    sections.forEach(section => {
      if (optionName && section.dataset.cartOption !== optionName) return

      const fieldBox = section.querySelector(`${this._selectors.discountOption}-field-box`)
      const results = section.querySelector(`${this._selectors.discountOption}-results`)
      const input = section.querySelector('form input')

      fieldBox.classList.add(CLASSES.isHidden)
      results.classList.remove(CLASSES.isHidden)
      input.value = ''
    })
  },
  _hideDiscountOptionSuccessfullState(optionName = null) {
    const sections = [].slice.call(document.querySelectorAll(this._selectors.discountOption))

    sections.forEach(section => {
      if (optionName && section.dataset.cartOption !== optionName) return

      const fieldBox = section.querySelector(`${this._selectors.discountOption}-field-box`)
      const results = section.querySelector(`${this._selectors.discountOption}-results`)

      fieldBox.classList.remove(CLASSES.isHidden)
      results.classList.add(CLASSES.isHidden)
    })
  },

  _showCertificateNotice() {
    const optionElem = document.querySelector(`${this._selectors.discountOption}[data-cart-option="${discountOptions.certificate}"]`)
    const noticeElem = optionElem.querySelector(`${this._selectors.discountOption}-notice`)

    noticeElem.classList.add(CLASSES.isVisible)
  },
  _hideCertificateNotice() {
    const optionElem = document.querySelector(`${this._selectors.discountOption}[data-cart-option="${discountOptions.certificate}"]`)
    const noticeElem = optionElem.querySelector(`${this._selectors.discountOption}-notice`)

    noticeElem.classList.remove(CLASSES.isVisible)
  },
  _showBonusNotice(count) {
    const optionElem = document.querySelector(`${this._selectors.discountOption}[data-cart-option="${discountOptions.bonus}"]`)
    const noticeElem = optionElem.querySelector(`${this._selectors.discountOption}-notice`)
    const countElem = optionElem.querySelector(`${this._selectors.discountOption}-notice-count`)

    noticeElem.classList.add(CLASSES.isVisible)
    countElem.textContent = count
  },
  _hideBonusNotice() {
    const optionElem = document.querySelector(`${this._selectors.discountOption}[data-cart-option="${discountOptions.bonus}"]`)
    const noticeElem = optionElem.querySelector(`${this._selectors.discountOption}-notice`)

    noticeElem.classList.remove(CLASSES.isVisible)
  },
  showBonusAlert() {
    const section = document.querySelector(`${this._selectors.discountOption}[data-cart-option="${discountOptions.bonus}"]`)
    const input = section.querySelector('input')
    const bonusPrompt = section.querySelector(`${this._selectors.discountOption}-prompt`)

    input.classList.add(CLASSES.isInvalid)
    bonusPrompt.classList.add(CLASSES.isInvalid)
  },
  _hideBonusAlert() {
    const section = document.querySelector(`${this._selectors.discountOption}[data-cart-option="${discountOptions.bonus}"]`)
    const input = section.querySelector('input')
    const bonusPrompt = section.querySelector(`${this._selectors.discountOption}-prompt`)

    input.classList.remove(CLASSES.isInvalid)
    bonusPrompt.classList.remove(CLASSES.isInvalid)
  },

  _changePricesInHTML(data, showCertificate = true) {
    const preResultsPriceContainer = document.querySelector(this._selectors.preResultsPrice)
    const fullPriceContainer = document.querySelector(this._selectors.fullPrice)
    const preResultsPricePromocode = document.querySelector(this._selectors.preResultsPricePromocode)
    const preResultsPricePromocodeDiscount = document.querySelector(`${this._selectors.preResultsPricePromocode}-discount`)
    const preResultsPricePromocodeContainer = document.querySelector(`${this._selectors.preResultsPricePromocode}-container`)
    const preResultsPriceCertificateDiscount = document.querySelector(`${this._selectors.preResultsPriceCertificate}-discount`)
    const preResultsPriceCertificateContainer = document.querySelector(`${this._selectors.preResultsPriceCertificate}-container`)
    const preResultsPriceBonusDiscount = document.querySelector(`${this._selectors.preResultsPriceBonus}-discount`)
    const preResultsPriceBonusContainer = document.querySelector(`${this._selectors.preResultsPriceBonus}-container`)

    preResultsPriceContainer.textContent = formatNumbers(+data.fullPrice.toFixed(0))

    if (data.promocode.discount.sum) {
      const percent = data.promocode.discount.discountPercent

      preResultsPricePromocodeDiscount.textContent = formatNumbers(data.promocode.discount.sum)
      preResultsPricePromocodeContainer.classList.remove(CLASSES.isHidden)

      if (percent !== 0) {
        preResultsPricePromocodeContainer.classList.remove(CLASSES.isHidden)
        preResultsPricePromocode.textContent = percent.toFixed(0)
      } else {
        preResultsPricePromocodeContainer.classList.add(CLASSES.isHidden)
        preResultsPricePromocode.textContent = '0'
      }
    } else {
      preResultsPricePromocodeDiscount.textContent = '0'
      preResultsPricePromocodeContainer.classList.add(CLASSES.isHidden)
    }

    if (data.certificate.discount) {
      preResultsPriceCertificateDiscount.textContent = formatNumbers(data.certificate.discount)
      preResultsPriceCertificateContainer.classList.remove(CLASSES.isHidden)

      if (showCertificate) {
        if (data.certificate.discount > data.fullPrice) {
          const btn = document.querySelector(`${this._selectors.discountOptions} button[data-target="[data-cart-option='${discountOptions.certificate}']"]`)
          this.openDiscountOptionsTab(btn)
          this._showCertificateNotice()
        } else {
          this._hideCertificateNotice()
        }
      }

    } else {
      preResultsPriceCertificateDiscount.textContent = '0'
      preResultsPriceCertificateContainer.classList.add(CLASSES.isHidden)
    }

    if (data.bonus.discount) {
      preResultsPriceBonusDiscount.textContent = formatNumbers(data.bonus.discount.current)
      preResultsPriceBonusContainer.classList.remove(CLASSES.isHidden)
    } else {
      preResultsPriceBonusDiscount.textContent = '0'
      preResultsPriceBonusContainer.classList.add(CLASSES.isHidden)
    }

    fullPriceContainer.textContent = formatNumbers(+data.finalPrice.toFixed(0))
  },

  _showPreResults() {
    const preResultsSection = document.querySelector(this._selectors.preResultsSection)

    if (preResultsSection) {
      preResultsSection.classList.add(CLASSES.isActive)
    }
  },
  _hidePreResults() {
    if (this._store.isPromocode() || this._store.isCertificate()) return

    const preResultsSection = document.querySelector(this._selectors.preResultsSection)

    if (preResultsSection) {
      preResultsSection.classList.remove(CLASSES.isActive)
    }
  },

  _showPromoWarnings() {
    const warnElems =
      [].slice.call(document.querySelectorAll(this._selectors.promocodeWarning))

    warnElems.forEach(elem => {
      elem.classList.add(CLASSES.isActive)
    })
  },
  _hidePromoWarnings() {
    const warnElems =
      [].slice.call(document.querySelectorAll(this._selectors.promocodeWarning))

    warnElems.forEach(elem => {
      elem.classList.remove(CLASSES.isActive)
    })
  },

  _clearView() {
    const cartItems = document.querySelector(this._selectors.cartItems)

    if (cartItems) cartItems.innerHTML = ''
  },

  _closeAllDiscountOptionsTabs() {
    const optionBtns =
      [].slice.call(document.querySelectorAll(`${this._selectors.discountOptions} button`))

    optionBtns.forEach(btn => {
      const btnTarget = document.querySelector(btn.dataset.target)

      btn.classList.remove(CLASSES.isActive)
      if (btnTarget) btnTarget.classList.remove(CLASSES.isActive)
    })
  },
  openDiscountOptionsTab(btn) {
    const btnTarget = document.querySelector(btn.dataset.target)

    this._closeAllDiscountOptionsTabs()

    btn.classList.add(CLASSES.isActive)
    if (btnTarget) btnTarget.classList.add(CLASSES.isActive)
  },

  /** Checks and disables mutually exclusive cart options (promo codes and bonuses) by turning off the buttons. */
  _disableMutuallyExclusiveOptions() {
    const promoTab = document.querySelector(`${this._selectors.discountOptionsBtn}[data-target="[data-cart-option='${discountOptions.promocode}']"]`)
    const bonusTab = document.querySelector(`${this._selectors.discountOptionsBtn}[data-target="[data-cart-option='${discountOptions.bonus}']"]`)

    if (bonusTab && this._store.isPromocode()) {
      bonusTab.disabled = true
    } else bonusTab.disabled = false

    if (promoTab && this._store.isBonus()) {
      promoTab.disabled = true
    } else promoTab.disabled = false
  },

  _verifyBonusPointsMaxUse(finalPrice) {
    const {bonusPoints} = this._store.getState().info

    if (bonusPoints && finalPrice >= 0) {
      const halfFinalPrice = Math.floor(finalPrice / 2)

      if (bonusPoints > halfFinalPrice) {
        this._showBonusNotice(halfFinalPrice)
      } else {
        this._hideBonusNotice()
      }
    }
  },

  _initDiscountOptionBtns() {
    const optionBtns =
      [].slice.call(document.querySelectorAll(`${this._selectors.discountOptions} button`))

    const handleOptions = (e) => {
      const btn = e.currentTarget

      if (btn.classList.contains(CLASSES.isActive)) {
        this._closeAllDiscountOptionsTabs()
        return
      }

      this.openDiscountOptionsTab(btn)
    }

    optionBtns.forEach(btn => {
      btn.addEventListener('click', handleOptions)
    })
  },
  _initButtonTooltipOpeners() {
    const closeAllPopups = () => {
      const btns = [].slice.call(
        document.querySelectorAll(`${this._selectors.btnsTooltipOpener} > button`)
      )

      btns.forEach(btn => {
        btn.classList.remove(CLASSES.isOpen)
      })
    }

    const onClickHandler = (e) => {
      const btn = e.target.closest(`${this._selectors.btnsTooltipOpener} > button`)

      if (btn) {
        closeAllPopups()
        btn.classList.toggle(CLASSES.isOpen)
      }
    }

    const onOutsideClickHandler = (e) => {
      if (!e.target.closest(this._selectors.btnsTooltipOpener)) {
        closeAllPopups()
      }
    }

    document.addEventListener('click', onClickHandler)
    document.addEventListener('click', onOutsideClickHandler)
  },
  _initAddProductButtons() {
    const onSubmitHandler = (e) => {
      const form = e.target.closest(this._selectors.productAddForm)

      if (form) {
        e.preventDefault()

        const isInCart = form.closest('.is-in-cart')
        if (isInCart) {
          this._openModal()
          return
        }

        const productId = form && form.product_id && form.product_id.value
        const inputs = [].slice.call(
          form.querySelectorAll('input[type="hidden"]:not([name="product_id"])')
        )
        const inputsValues = inputs.map(input => [input.name, input.value])
        const extraData = inputsValues.length ? objectFromEntries(inputsValues) : null

        if (productId) this._store.addProduct(productId, extraData, () => {
          this._openModal()
        })
      }
    }

    document.addEventListener('submit', onSubmitHandler)
  },
  _initRemoveProductButtons() {
    const removeCartItem = (e) => {
      const removeBtn = e.target.closest(this._selectors.cartItemRemoveBtn)

      if (removeBtn) {
        const cartItem = removeBtn.closest(this._selectors.cartItem)
        const id = cartItem.dataset.productId

        this._store.removeProduct(id)
      }
    }

    document.addEventListener('click', removeCartItem)
  },
  _initAddToFavoriteProductButtons() {
    const favoriteBtns =
      [].slice.call(document.querySelectorAll(this._selectors.cartItemFavoriteBtn))

    favoriteBtns.forEach(btn => {
      const id = btn.closest('[data-product-id]').dataset.productId

      if (id) {
        btn.classList.add(`favorite_prod_${id}`)
        // eslint-disable-next-line no-param-reassign
        btn.dataset.prodId = id
      }
    })
  },
  _initChangeCountInput() {
    const changeEventHandler = (e) => {
      const countInput = e.target.closest(this._selectors.cartItemCount)

      if (countInput) {
        const input = countInput.querySelector('input')
        const value = +input.value
        const cartItem = input.closest(this._selectors.cartItem)
        const id = cartItem.dataset.productId

        this._store.changeProductCount(id, value)
      }
    }

    document.addEventListener('change', changeEventHandler)
  },
  _initDiscountOptions() {
    const sections = [].slice.call(document.querySelectorAll(this._selectors.discountOption))

    sections.forEach(section => {
      const sectionName = section.dataset.cartOption
      if (!sectionName) return

      const form = section.querySelector('form')
      const input = form.querySelector('input')
      const btn = form.querySelector('button')
      const results = section.querySelector(`${this._selectors.discountOption}-results`)
      const removeBtn = results.querySelector('button')
      const warning = section.querySelector(`${this._selectors.discountOption}-warn`)
      const warningValue = section.querySelector(`${this._selectors.discountOption}-warn-value`)
      const promoDiscountedWarning = section.querySelector(`${this._selectors.discountOption}-discounted-warn`)
      const promoDiscountedWarningValue = section.querySelector(`${this._selectors.discountOption}-discounted-warn-value`)

      let input2
      let warningValue2
      let emptyWarning1
      let emptyWarning21
      let emptyWarning22
      let emptyWarning23

      if (sectionName === discountOptions.certificate) {
        input2 = form.querySelector('input:nth-of-type(2)')
        warningValue2 = section.querySelector(`${this._selectors.discountOption}-warn-value-2`)
        emptyWarning21 = section.querySelector(`${this._selectors.discountOption}-warn-empty-2-1`)
        emptyWarning22 = section.querySelector(`${this._selectors.discountOption}-warn-empty-2-2`)
        emptyWarning23 = section.querySelector(`${this._selectors.discountOption}-warn-empty-2-3`)
      } else if (sectionName === discountOptions.promocode || sectionName === discountOptions.bonus) {
        emptyWarning1 = section.querySelector(`${this._selectors.discountOption}-warn-empty-1`)
      }

      const closeAllWarnigs = () => {
        if (warning) warning.classList.remove(CLASSES.isActive)
        if (warningValue) warningValue.textContent = ''

        if (input2 && warningValue2) {
          warningValue2.textContent = ''
        }

        if (promoDiscountedWarning) {
          promoDiscountedWarning.classList.remove(CLASSES.isActive)
          promoDiscountedWarningValue.textContent = ''
        }
      }

      const removeEmptinessWarnings = () => {
        input.classList.remove(CLASSES.isInvalid)
        if (input2) input2.classList.remove(CLASSES.isInvalid)
        if (emptyWarning1) emptyWarning1.classList.remove(CLASSES.isActive)
        if (emptyWarning21) emptyWarning21.classList.remove(CLASSES.isActive)
        if (emptyWarning22) emptyWarning22.classList.remove(CLASSES.isActive)
        if (emptyWarning23) emptyWarning23.classList.remove(CLASSES.isActive)
      }

      const handleSubmit = (e) => {
        e.preventDefault()

        removeEmptinessWarnings()

        let value
        if (!input2) {
          ({value} = input)
        } else {
          value = {
            id: input.value,
            code: input2.value,
          }
        }

        if (sectionName === discountOptions.bonus) {
          value = Math.max(Math.abs(value), 1)

          const halfFinalPrice = Math.floor(window.cartFinalPrice / 2)

          if (value > halfFinalPrice) {
            this.showBonusAlert()

            return
          }

          this._hideBonusAlert()
        }

        const isInputValueEmpty = val => {
          if (val.trim() === '') return true
          return false
        }

        if (!input2) {
          if (isInputValueEmpty(input.value)) {
            closeAllWarnigs()
            input.classList.add(CLASSES.isInvalid)
            input.focus()
            emptyWarning1.classList.add(CLASSES.isActive)
            return
          }
        } else {
          if (isInputValueEmpty(input.value) && isInputValueEmpty(input2.value)) {
            closeAllWarnigs()
            input.classList.add(CLASSES.isInvalid)
            input.focus()
            input2.classList.add(CLASSES.isInvalid)
            emptyWarning21.classList.add(CLASSES.isActive)
            return
          }

          if (isInputValueEmpty(input.value)) {
            closeAllWarnigs()
            input.classList.add(CLASSES.isInvalid)
            input.focus()
            emptyWarning22.classList.add(CLASSES.isActive)
            return
          }

          if (isInputValueEmpty(input2.value)) {
            closeAllWarnigs()
            input2.classList.add(CLASSES.isInvalid)
            input2.focus()
            emptyWarning23.classList.add(CLASSES.isActive)
            return
          }
        }

        // eslint-disable-next-line no-shadow
        const callback = data => {
          if (data.status === 'success') {

            const openPromoDiscountedWarning = () => {
              promoDiscountedWarning.classList.add(CLASSES.isActive)
              promoDiscountedWarningValue.textContent = value

              setTimeout(this._showPromoWarnings.bind(this), 0)
            }

            const openWarning = () => {
              warning.classList.add(CLASSES.isActive)

              if (input2 && warningValue2) {
                warningValue.textContent = value.id
                warningValue2.textContent = value.code
              } else {
                warningValue.textContent = value
              }
            }

            if (data.discount !== null) {
              closeAllWarnigs()
            } else if (data.inapplicable) {
              closeAllWarnigs()
              openPromoDiscountedWarning()
            } else {
              closeAllWarnigs()
              openWarning()
            }

            input.value = ''
            if (input2) input2.value = ''
            btn.disabled = false
          }
        }

        btn.disabled = true

        this._store.changeDiscount(value, sectionName, callback)
      }

      const removeInstance = () => {
        this._store.changeDiscount(null, sectionName, data => {
          if (data.status === 'success') {
            this._hideDiscountOptionSuccessfullState(sectionName)
          }
        })
      }

      form.addEventListener('submit', handleSubmit)

      removeBtn.addEventListener('click', removeInstance)
    })
  },
  _initPromocodeWarnings() {
    const closeWarning = (e) => {
      const warnElem = e.target.closest(this._selectors.promocodeWarning)

      if (warnElem) {
        warnElem.classList.remove(CLASSES.isActive)
      }
    }

    document.addEventListener('click', closeWarning)
  },
  _syncChageable1AddFormsWithCart(id = null, all = false) {
    const forms =
      [].slice.call(document.querySelectorAll(this._selectors.productAddFormChangeable1))
    const { products } = this._store.getState()

    const toggleFormInCartState = (form, remove = false) => {
      const productItem = form.closest('.product-thumb')

      if (!productItem) return

      const state = this._store.getState()
      const btn = form.querySelector('button.product-thumb__main-button')

      if (!productItem.matches('.is-in-cart') && !remove) {

        productItem.classList.add('is-in-cart')
        productItem.classList.remove('is-in-stock')
        if (btn) btn.textContent = state.info.popups.inCart[lang].text

        // if tooltips are needed
        // const btn = form.querySelector('button')
        // form.classList.add('js-tooltip')
        // btn.insertAdjacentHTML('afterend', `
        //   <div class="product-thumb__cart-tooltip js-tooltip-content${` ${this._selectors.productAddFormTooltip.slice(1)}`}">
        //     ${ this._store.getState().products[0].popups.inCart[lang].text }
        //   </div>
        // `)
      } else {
        productItem.classList.remove('is-in-cart')
        productItem.classList.add('is-in-stock')
        if (btn) btn.textContent = state.info.popups.notInCart[lang].text

        // if tooltips are needed
        // form.classList.remove('js-tooltip')
        // form.querySelector(this._selectors.productAddFormTooltip).remove()
        // if (form._tippy) form._tippy.destroy()
      }
    }

    // if you only need to change one element
    if (id) {
      forms.forEach(form => {
        const formID = form.querySelector('[name="product_id"]').value

        if (formID && formID.toString().trim() === id.toString().trim()) {
          toggleFormInCartState(form)
        }
      })

      return
    }

    // if you need to change every element
    // for example when emptying the entire shopping cart
    if (all) {
      forms.forEach(form => {
        toggleFormInCartState(form, true)
      })

      return
    }

    // if you need to change only those that match the current ones in the cart
    forms.forEach(form => {
      const formID = form.querySelector('[name="product_id"]').value

      if (formID) {
        products.forEach(product => {
          if (formID.toString().trim() === product.id.toString().trim()) {
            toggleFormInCartState(form)
          }
        })
      }
    })
  },
  _syncChageable2AddFormsWithCart(id = null, all = false) {
    const forms =
      [].slice.call(document.querySelectorAll(this._selectors.productAddFormChangeable2))
    const state = this._store.getState()

    const toggleFormInCartState = (form, remove = false) => {
      const btn = form.querySelector('button')
      if (!btn) return

      if (!form.matches('.is-in-cart') && !remove) {
        form.classList.add('is-in-cart')
        btn.classList.add('cta--outline')
        btn.textContent = state.info.popups.inCart[lang].text
      } else {
        form.classList.remove('is-in-cart')
        btn.classList.remove('cta--outline')
        btn.textContent = state.info.popups.notInCart[lang].text
      }
    }

    // if you only need to change one element
    if (id) {
      forms.forEach(form => {
        const formID = form.querySelector('[name="product_id"]').value

        if (formID && formID.toString().trim() === id.toString().trim()) {
          toggleFormInCartState(form)
        }
      })

      return
    }

    // if you need to change every element
    // for example when emptying the entire shopping cart
    if (all) {
      forms.forEach(form => {
        toggleFormInCartState(form, true)
      })

      return
    }

    // if you need to change only those that match the current ones in the cart
    forms.forEach(form => {
      const formID = form.querySelector('[name="product_id"]').value

      if (formID) {
        state.products.forEach(product => {
          if (formID.toString().trim() === product.id.toString().trim()) {
            toggleFormInCartState(form)
          }
        })
      }
    })
  },
  _syncAllAddForms(id = null, all = false) {
    this._syncChageable1AddFormsWithCart(id, all)
    this._syncChageable2AddFormsWithCart(id, all)
  },
  render(cartStorageData, action = {}) {
    switch(action.type) {

      case updateActions.addProduct:
        this._insertNewProductsItemHTML(action.payload)

        if (cartStorageData.isPromocode) {
          this._showPreResults()
          this._changePromoTextInHTML(cartStorageData.promocode.value)
          this._showPromoWarnings()
        }

        if (cartStorageData.isCertificate) {
          this._showPreResults()
          this._changeCertificateTextInHTML(cartStorageData.certificate.discount)
        }

        if (cartStorageData.isBonus) {
          this._showPreResults()
          this._changeBonusTextInHTML(cartStorageData.certificate.discount.current)
        }

        this._syncAllAddForms(action.payload.id)
        this._initAddToFavoriteProductButtons()
        this._hideBonusAlert()
        break

      case updateActions.removeProduct:
        this._removeProductFromHTML(action.payload)
        this._syncAllAddForms(action.payload)
        this._hideBonusAlert()
        break

      case updateActions.changeProductCount:
        this._changeProductCountInHTML(action.payload.id, action.payload.count)
        this._changeProductPriceInHTML(
            action.payload.id,
            action.payload.count,
            cartStorageData.products.find(product => product.id === action.payload.id).prices.current
          )
        this._hideBonusAlert()
        break

      case updateActions.changePromocode:
        if (cartStorageData.isPromocode) {
          this._showPreResults()
          this._changePromoTextInHTML(cartStorageData.promocode.value)
          this._showPromoWarnings()
          this._showDiscountOptionSuccessfullState(discountOptions.promocode)
        } else {
          this._hidePreResults()
          this._changePromoTextInHTML(cartStorageData.promocode.value)
          this._hidePromoWarnings()
        }
        break

      case updateActions.changeCertificate:
        if (cartStorageData.isCertificate) {
          this._showPreResults()
          this._changeCertificateTextInHTML(cartStorageData.certificate.discount)
          this._showDiscountOptionSuccessfullState(discountOptions.certificate)
        } else {
          this._hidePreResults()
          this._changeCertificateTextInHTML(cartStorageData.certificate.discount)
          this._hideCertificateNotice()
        }
        break

      case updateActions.changeBonus:
        if (cartStorageData.isBonus) {
          this._showPreResults()
          this._changeBonusTextInHTML(cartStorageData.bonus.discount.current)
          this._showDiscountOptionSuccessfullState(discountOptions.bonus)
        } else {
          this._hidePreResults()
          this._changeBonusTextInHTML('0')
          this._hideDiscountOptionSuccessfullState(discountOptions.bonus)
        }
        break

      case updateActions.clearAll:
        this._clearView()
        this._hidePreResults()
        this._changePromoTextInHTML('')
        this._changeCertificateTextInHTML('')
        this._changeBonusTextInHTML('')
        this._hideDiscountOptionSuccessfullState()
        this._syncAllAddForms(null, true)
        break

      default:
        if (!cartStorageData.isEmpty) {
          cartStorageData.products.forEach(product => {
            this._insertNewProductsItemHTML(product)
          })

          this._syncAllAddForms(null, false)

          if (cartStorageData.isPromocode) {
            this._showPreResults()
            this._changePromoTextInHTML(cartStorageData.promocode.value)
            this._changeCertificateTextInHTML(cartStorageData.certificate.discount)
            this._showDiscountOptionSuccessfullState(discountOptions.promocode)
            this._showPromoWarnings()
          }

          if (cartStorageData.isCertificate) {
            this._showPreResults()
            this._changeCertificateTextInHTML(cartStorageData.certificate.discount)
            this._showDiscountOptionSuccessfullState(discountOptions.certificate)
          }

          if (cartStorageData.isBonus) {
            this._showPreResults()
            this._changeCertificateTextInHTML(cartStorageData.certificate.discount)
            this._changeBonusTextInHTML(cartStorageData.bonus.discount.current)
            this._showDiscountOptionSuccessfullState(discountOptions.bonus)
          }

          this._initAddToFavoriteProductButtons()
        }
    }

    if (action.type === updateActions.changePromocode) {
      this._changePricesInHTML(cartStorageData, false)
    } else {
      this._changePricesInHTML(cartStorageData)
    }

    this._verifyBonusPointsMaxUse(cartStorageData.finalPrice)
    window.cartFinalPrice = cartStorageData.finalPrice

    this._disableMutuallyExclusiveOptions()
    this._changeWhetherEmpty()
  },
  init(store) {
    this._store = store
    this._store.subscribe(this.render.bind(this))

    this._initDiscountOptionBtns()
    this._initButtonTooltipOpeners()
    this._initAddProductButtons()
    this._initRemoveProductButtons()
    this._initChangeCountInput()
    this._initDiscountOptions()
    this._initPromocodeWarnings()

    this._syncAllAddForms()
  },
}

const cartPopupView = {
  _maxProducts: 2,
  _selectors: {
    cartPopup: '.js-cart-popup',
    cartPopupList: '.js-cart-popup-list',
    cartPopupListItem: '.js-cart-popup-list-item',
    cartPopupListItemCount: '.js-cart-popup-list-item-count',
    cartPopupListItemPrice: '.js-cart-popup-list-item-price',
    cartPopupListItemOldPrice: '.js-cart-popup-list-item-old-price',
    cartPopupCount: '.js-cart-popup-count',
    cartPopupResidualCount: '.js-cart-popup-residual-сount',
    cartPopupTotalCount: '.js-cart-popup-total-count',
    cartPopupResidualEnding: '.js-cart-popup-residual-ending',
    cartPopupTotalEnding: '.js-cart-popup-total-ending',
    cartPopupFinalPrice: '.js-cart-popup-final-price',
    cartBadgeCount: '.js-cart-badge-count',
  },
  _changeWhetherEmpty() {
    const cartPopups = [].slice.call(document.querySelectorAll(this._selectors.cartPopup))

    if (this._store.isEmpty()) {
      cartPopups.forEach(popup => {
        popup.classList.remove(CLASSES.isOpen)
      })
    } else {
      cartPopups.forEach(popup => {
        popup.classList.add(CLASSES.isOpen)
      })
    }
  },
  _hideUnnecessaryItems() {
    setTimeout(() => {
      const $cartList = $(this._selectors.cartPopupList)
      const $cartListMore = $(this._selectors.cartPopupCount)

      $cartList.each((i, cartEl) => {
        const previewElems = $(cartEl).find(this._selectors.cartPopupListItem)

        if (previewElems.length > this._maxProducts) {
          let maxHeight = 0

          previewElems.filter(`:lt(${this._maxProducts})`).each((index, el) => {
            maxHeight += $(el).outerHeight()
          })

          $(cartEl).css({'max-height': maxHeight})
          $cartListMore.addClass(CLASSES.isVisible)
        } else {
          $(cartEl).css({'max-height': 'none'})
          $cartListMore.removeClass(CLASSES.isVisible)
        }
      })
    }, 0)
  },

  _insertNewProductsItemHTML(product) {
    const containers = [].slice.call(document.querySelectorAll(this._selectors.cartPopupList))

    const cartState = this._store.getState()

    const price = product.prices.current * product.count

    const priceMarkup = product.prices.old ? `
      <div class="cart-product__price">
        <b class="js-cart-popup-list-item-price">${ formatNumbers(price) }</b> ${ cartState.info.currency }
        <span class="js-cart-popup-list-item-old-price">${ formatNumbers(product.prices.old * product.count) } ${ cartState.info.currency }</span>
      </div>` : `
      <div class="cart-product__price">
        <b class="js-cart-popup-list-item-price">${ formatNumbers(price) }</b> ${ cartState.info.currency }
      </div>`

    const itemMarkup = `
      <div class="cart__item cart-product js-cart-popup-list-item" data-product-id="${ product.id }">
        <div class="cart-product__image">
          <a href="${ product.titleLink }">
            <img class="img-responsive" src="${ product.images.small.src }"/>
          </a>
        </div>
        <div class="cart-product__body">
          <h3 class="cart-product__title">
            <a href="${ product.titleLink }">${ product.title }</a>
          </h3>
          <div class="cart-product__price-block">
            <div class="cart-product__count"><span class="js-cart-popup-list-item-count">${ product.count }</span> шт</div>
            ${ priceMarkup }
          </div>
        </div>
      </div>
    `

    containers.forEach(container => {
      container.insertAdjacentHTML('beforeend', itemMarkup)
    })
  },
  _removeProductFromHTML(id) {
    const popupItems = [].slice.call(document.querySelectorAll(this._selectors.cartPopupListItem))

    popupItems.forEach(item => {
      if (item.dataset.productId === id) {
        item.remove()
      }
    })
  },
  _changeProductCountInHTML(id, count) {
    const cartItems =
     [].slice.call(document.querySelectorAll(`${this._selectors.cartPopupListItem}[data-product-id="${id}"`))

    cartItems.forEach(item => {
      const itemCount = item.querySelector(this._selectors.cartPopupListItemCount)

      itemCount.textContent = count
    })
  },
  _changeProductPriceInHTML(id, count, price, oldPrice, currency) {
    const cartItems =
      [].slice.call(document.querySelectorAll(`${this._selectors.cartPopupListItem}[data-product-id="${id}"`))

    cartItems.forEach(item => {
      const itemPrice = item.querySelector(this._selectors.cartPopupListItemPrice)
      const itemOldPrice = item.querySelector(this._selectors.cartPopupListItemOldPrice)

      itemPrice.textContent = formatNumbers(price * count)

      if (itemOldPrice && oldPrice) itemOldPrice.textContent = `${formatNumbers(oldPrice * count)} ${currency}`
    })
  },
  _changeAllProductsPricesInHTML(products) {
    const filteredProducts = products.filter(product => {
      return !product.prices.old
    })

    filteredProducts.forEach(product => {
      this._changeProductPriceInHTML(product.id, product.count, product.prices.current, null, product.prices.currency)
    })
  },
  _changePricesInHTML(finalPrice) {
    const finalPriceContainers =
      [].slice.call(document.querySelectorAll(this._selectors.cartPopupFinalPrice))

    finalPriceContainers.forEach(container => {
      // eslint-disable-next-line no-param-reassign
      container.textContent = formatNumbers(+finalPrice.toFixed(0))
    })
  },
  _changeCounters(count) {
    const residualCountContainers = [].slice.call(document.querySelectorAll(this._selectors.cartPopupResidualCount))
    const totalCountContainers = [].slice.call(document.querySelectorAll(this._selectors.cartPopupTotalCount))
    const residualEndingContainers = [].slice.call(document.querySelectorAll(this._selectors.cartPopupResidualEnding))
    const totalEndingContainers = [].slice.call(document.querySelectorAll(this._selectors.cartPopupTotalEnding))

    const badgeCountContainers = [].slice.call(document.querySelectorAll(this._selectors.cartBadgeCount))

    /* eslint-disable no-param-reassign */
    residualCountContainers.forEach(container => {
      container.textContent = count - this._maxProducts
    })
    totalCountContainers.forEach(container => {
      container.textContent = count
    })
    residualEndingContainers.forEach(container => {
      container.textContent = defineEndings(count - this._maxProducts)
    })
    totalEndingContainers.forEach(container => {
      container.textContent = defineEndings(count)
    })

    badgeCountContainers.forEach(container => {
      container.textContent = count
    })
    /* eslint-enable no-param-reassign */
  },
  _changeCartBadgeVisibility() {
    const badges = [].slice.call(document.querySelectorAll(this._selectors.cartBadgeCount))

    badges.forEach(badge => {
      if (!this._store.isEmpty()) {
        badge.classList.remove(CLASSES.isHidden)
      } else {
        badge.classList.add(CLASSES.isHidden)
      }
    })
  },

  _clearView() {
    const popupList = [].slice.call(document.querySelectorAll(this._selectors.cartPopupList))

    popupList.forEach(list => {
      // eslint-disable-next-line no-param-reassign
      list.innerHTML = ''
    })
  },

  _initPopup() {
    const $cartList = $(this._selectors.cartPopupList)
    const $cartListMore = $(this._selectors.cartPopupCount)

    if (!$cartListMore.length) return

    $cartListMore.on('click', (e) => {
      const $btn = $(e.currentTarget)
      $cartList.toggleClass(CLASSES.isExpanded)
      $btn.toggleClass(CLASSES.isExpanded)
    })
  },
  render(cartStorageData, action = {}) {
    switch(action.type) {
      case updateActions.addProduct:
        this._insertNewProductsItemHTML(action.payload)
        this._hideUnnecessaryItems()
        break

      case updateActions.removeProduct:
        this._removeProductFromHTML(action.payload)
        this._hideUnnecessaryItems()
        break

      case updateActions.changeProductCount:
        this._changeProductCountInHTML(action.payload.id, action.payload.count)
        this._changeProductPriceInHTML(
          action.payload.id,
          action.payload.count,
          cartStorageData.products.find(product => product.id === action.payload.id).prices.current,
          cartStorageData.products.find(product => product.id === action.payload.id).prices.old,
          cartStorageData.products.find(product => product.id === action.payload.id).prices.currency,
        )
        break

      case updateActions.changePromocode:
      case updateActions.changeCertificate:
        this._changeAllProductsPricesInHTML(cartStorageData.products)
        break

      case updateActions.clearAll:
        this._clearView()
        break

      default:
        if (!cartStorageData.isEmpty) {
          cartStorageData.products.forEach(product => {
            this._insertNewProductsItemHTML(product)
          })
          this._hideUnnecessaryItems()
        }
    }

    this._changePricesInHTML(cartStorageData.finalPrice)
    this._changeCounters(cartStorageData.products.length)

    this._changeCartBadgeVisibility()
    this._changeWhetherEmpty()
  },
  init(store) {
    this._store = store
    this._store.subscribe(this.render.bind(this))

    this._initPopup()
  },
}

const checkoutView = {
  _selectors: {
    checkout: '.js-cart-checkout',
    checkoutProductsCount: '.js-cart-checkout-products-count',
    checkoutProductsEndings: '.js-cart-checkout-products-count-total-ending',
    checkoutProductsFinalPrice: '.js-cart-checkout-products-final-price',
    checkoutProducts: '.js-cart-checkout-products',
    checkoutProduct: '.js-cart-checkout-product',
    checkoutProductCount: '.js-cart-checkout-product-count',
    checkoutProductPrice: '.js-cart-checkout-product-price',
    checkoutProductsDiscount: '.js-cart-checkout-products-discount',
  },
  _checkCheckout() {
    const checkout = document.querySelector(this._selectors.checkout)
    return !!checkout
  },

  _insertNewProductsItemHTML(product) {
    const container = document.querySelector(this._selectors.checkoutProducts)
    if (!container) return

    const cartState = this._store.getState()

    const price = product.prices.current * product.count

    const itemMarkup = `
      <div class="checkout-product js-cart-checkout-product" data-product-id="${ product.id }">
        <a href="${ product.titleLink }" class="checkout-product__name">
          <span>${ product.title }</span>
        </a>
        <div class="checkout-product__content">
          <a href="${ product.titleLink }" class="checkout-product__image">
            <img src="${ product.images.small.src }" alt="${ product.images.small.alt }">
          </a>
          <div class="checkout-product__info">
            <p><span class="js-cart-checkout-product-count">${ product.count }<span> шт</p>
            <p><span class="js-cart-checkout-product-price">${ formatNumbers(price) }</span> ${ cartState.info.currency }</p>
          </div>
        </div>
      </div>
    `

    container.insertAdjacentHTML('beforeend', itemMarkup)
  },
  _removeProductFromHTML(id) {
    const items = document.querySelectorAll(this._selectors.checkoutProduct)

    items.forEach(item => {
      if (item.dataset.productId === id) {
        item.remove()
      }
    })
  },
  _changeProductCountInHTML(id, count) {
    const item =
      document.querySelector(`${this._selectors.checkoutProduct}[data-product-id="${id}"`)

    if (item) {
      const itemCount = item.querySelector(this._selectors.checkoutProductCount)
      itemCount.textContent = count
    }
  },
  _changeProductPriceInHTML(id, count, price) {
    const item =
      document.querySelector(`${this._selectors.checkoutProduct}[data-product-id="${id}"`)

    const itemPrice = item.querySelector(this._selectors.checkoutProductPrice)

    itemPrice.textContent = formatNumbers(price * count)
  },
  _changeAllProductsPricesInHTML(products) {
    products.forEach(product => {
      this._changeProductPriceInHTML(product.id, product.count, product.prices.current)
    })
  },
  _changePricesInHTML(fullPrice, finalPrice) {
    const finalPriceContainers =
      [].slice.call(document.querySelectorAll(this._selectors.checkoutProductsFinalPrice))
    const discountElem = document.querySelector(this._selectors.checkoutProductsDiscount)
    const discountContainer = document.querySelector(`${this._selectors.checkoutProductsDiscount}-container`)

    const priceDifference = fullPrice - finalPrice

    if (priceDifference === 0) {
      discountContainer.classList.add(CLASSES.isHidden)
    } else {
      discountContainer.classList.remove(CLASSES.isHidden)
      discountElem.textContent = formatNumbers(+priceDifference.toFixed(0))
    }

    finalPriceContainers.forEach(container => {
      // eslint-disable-next-line no-param-reassign
      container.textContent = formatNumbers(+finalPrice.toFixed(0))
    })
  },
  _changeCounters(count) {
    const totalCountContainer = document.querySelector(this._selectors.checkoutProductsCount)
    const totalEndingContainer = document.querySelector(this._selectors.checkoutProductsEndings)

    /* eslint-disable no-param-reassign */
    totalCountContainer.textContent = count
    totalEndingContainer.textContent = defineEndings(count)
    /* eslint-enable no-param-reassign */
  },

  _clearView() {
    const productsList = document.querySelector(this._selectors.checkoutProduct)

    // eslint-disable-next-line no-param-reassign
    productsList.innerHTML = ''
  },

  render(cartStorageData, action = {}) {
    switch(action.type) {
      case updateActions.addProduct:
        this._insertNewProductsItemHTML(action.payload)
        break

      case updateActions.removeProduct:
        this._removeProductFromHTML(action.payload)
        break

      case updateActions.changeProductCount:
        this._changeProductCountInHTML(action.payload.id, action.payload.count)
        this._changeProductPriceInHTML(
          action.payload.id,
          action.payload.count,
          cartStorageData.products.find(product => product.id === action.payload.id).prices.current,
        )
        break

      case updateActions.changePromocode:
      case updateActions.changeCertificate:
        this._changeAllProductsPricesInHTML(cartStorageData.products)
        break

      case updateActions.clearAll:
        this._clearView()
        break

      default:
        if (!cartStorageData.isEmpty) {
          cartStorageData.products.forEach(product => {
            this._insertNewProductsItemHTML(product)
          })
        }
    }

    this._changePricesInHTML(cartStorageData.fullPrice, cartStorageData.finalPrice)
    this._changeCounters(cartStorageData.products.length)
  },
  init(store) {
    if (!this._checkCheckout()) return

    this._store = store
    this._store.subscribe(this.render.bind(this))
  },
}

window.cartStore = cartStore
window.cartModalView = cartModalView

export default function initCartModal() {
  const modal = document.querySelector(cartModalView._selectors.modal)
  // eslint-disable-next-line no-useless-return
  if (!modal) return

  cartStore.init()
  cartModalView.init(cartStore)
  cartPopupView.init(cartStore)
  checkoutView.init(cartStore)
}

/* eslint-disable no-underscore-dangle */
